import {z} from 'zod';

const DIGITS_ONLY = /^\d+$/;
const FEIN_REGEX = /^\d{9}$/;
const SIC_REGEX = /^\d{4}$/;
const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const STATE_REGEX = /^[A-Za-z]{2}$/;
const PHONE_REGEX = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

const AddressDraftSchema = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
});

const ContactDraftSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  title: z.string(),
  phone: z.string(),
  email: z.string(),
});

function getAddressFinalSchema(errorPrefix: string) {
  return z.object({
    street: z
      .string()
      .min(1, `${errorPrefix}: Street address is required`)
      .max(200, `${errorPrefix}: Street address is too long`),
    city: z
      .string()
      .min(1, `${errorPrefix}: City is required`)
      .max(100, `${errorPrefix}: City name is too long`),
    state: z.string().regex(STATE_REGEX, `${errorPrefix}: State must be a 2-letter code`),
    zip: z
      .string()
      .regex(
        ZIP_REGEX,
        `${errorPrefix}: ZIP must be 5 digits or ZIP+4 (e.g., 12345 or 12345-6789)`,
      ),
  });
}

function getOptionalAddressFinalSchema(errorPrefix: string) {
  return z.object({
    street: z.string().max(200, `${errorPrefix}: Street address is too long`),
    city: z.string().max(100, `${errorPrefix}: City name is too long`),
    state: z.union([
      z.literal(''),
      z.string().regex(STATE_REGEX, `${errorPrefix}: State must be a 2-letter code`),
    ]),
    zip: z.union([
      z.literal(''),
      z
        .string()
        .regex(
          ZIP_REGEX,
          `${errorPrefix}: ZIP must be 5 digits or ZIP+4 (e.g., 12345 or 12345-6789)`,
        ),
    ]),
  });
}

function getOptionalPhoneFinalSchema(errorPrefix: string) {
  return z.union([
    z.literal(''),
    z
      .string()
      .regex(PHONE_REGEX, `${errorPrefix}: Phone must be a valid US phone number or left blank`),
  ]);
}

// Required contact: first name, last name, and email must be present; title and
// phone are optional but validated when provided.
function getRequiredContactFinalSchema(errorPrefix: string) {
  return z.object({
    firstName: z
      .string()
      .min(1, `${errorPrefix}: First name is required`)
      .max(100, `${errorPrefix}: First name is too long`),
    lastName: z
      .string()
      .min(1, `${errorPrefix}: Last name is required`)
      .max(100, `${errorPrefix}: Last name is too long`),
    title: z.string().max(100, `${errorPrefix}: Title is too long`),
    phone: getOptionalPhoneFinalSchema(errorPrefix),
    email: z.string().email(`${errorPrefix}: Email must be valid`),
  });
}

// Optional contact: every field may be left blank; email and phone are
// validated only when provided.
function getOptionalContactFinalSchema(errorPrefix: string) {
  return z.object({
    firstName: z.string().max(100, `${errorPrefix}: First name is too long`),
    lastName: z.string().max(100, `${errorPrefix}: Last name is too long`),
    title: z.string().max(100, `${errorPrefix}: Title is too long`),
    phone: getOptionalPhoneFinalSchema(errorPrefix),
    email: z.union([
      z.literal(''),
      z.string().email(`${errorPrefix}: Email must be a valid email or left blank`),
    ]),
  });
}

export const AchFormDraftSchema = z.object({
  bankAccount: z.object({
    businessNameOnAccount: z.string(),
    accountHolderFirstName: z.string(),
    accountHolderLastName: z.string(),
    contactPhone: z.string(),
    contactEmail: z.string(),
    address: AddressDraftSchema,
  }),
  achDetails: z.object({
    bankName: z.string(),
    bankAddress: AddressDraftSchema,
    routingNumber: z.string(),
    accountNumber: z.string(),
  }),
  authorization: z.object({
    authorizedSignature: z.string(),
    signatureDate: z.string(),
  }),
});

const LocationDraftSchema = z.object({
  locationName: z.string(),
  fein: z.string(),
  payrollLocationCode: z.string().default(''),
  companyAddress: AddressDraftSchema,
  mailingAddress: AddressDraftSchema,
  contactPerson: ContactDraftSchema,
  achForm: AchFormDraftSchema,
});

export const GroupInfoDraftSchema = z.object({
  benefitYear: z.string(),
  companyName: z.string(),
  companyDba: z.string(),
  otherKnownName: z.string(),
  sic: z.string(),
  stateOfDoingBusiness: z.string().default(''),
  totalEmployees: z.number({
    invalid_type_error: 'Total employees cannot be blank',
    required_error: 'Total employees cannot be blank',
  }),
  numberOfLocations: z.number({
    invalid_type_error: 'Number of locations cannot be blank',
    required_error: 'Number of locations cannot be blank',
  }),
  locations: z.array(LocationDraftSchema),
});

// Allowed waiting period choices, in days. Surfaced as a dropdown in the wizard.
export const ALLOWED_WAITING_PERIOD_DAYS = [14, 15, 30, 60] as const;
export type AllowedWaitingPeriodDays = (typeof ALLOWED_WAITING_PERIOD_DAYS)[number];

export const PAYROLL_FREQUENCIES = [
  'WEEKLY',
  'BI_WEEKLY',
  'BI_MONTHLY',
  'MONTHLY',
  'SEMI_MONTHLY',
  'WEEKLY_52',
  'WEEKLY_48',
] as const;

export type PayrollFrequency = (typeof PAYROLL_FREQUENCIES)[number];

export const AdminDetailsDraftSchema = z.object({
  eligibility: z.object({
    fullTime: z.boolean(),
    partTime: z.boolean(),
  }),
  waitingPeriod: z.number({invalid_type_error: 'Waiting period must be a number'}).nullish(),
  payrollFrequencies: z.array(z.enum(PAYROLL_FREQUENCIES)),
  enrollmentCompany: z.string(),
  enrollmentType: z.enum(['AUTO_ENROLL', 'VOLUNTARY']),
  billingPreOrPostTax: z.enum(['PRE_TAX', 'POST_TAX']),
  benefitsPreOrPostTax: z.enum(['PRE_TAX', 'POST_TAX']),
});

export const ContactsDraftSchema = z.object({
  authorizedSigner: ContactDraftSchema,
  eligibilityContact: ContactDraftSchema,
  billingContact: ContactDraftSchema,
});

export const PayrollInfoDraftSchema = z.object({
  contact: ContactDraftSchema,
  vendor: z.string(),
  payrollVendorContact: ContactDraftSchema,
  system: z.string(),
  payrollCompanyId: z.string(),
});

// read/write only available for internal/client admins
export const AgentOfRecordDraftSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  email: z.string(),
  additionalPhone: z.string(),
  additionalEmail: z.string(),
  // Optional: token-flow reads strip PEPM (restoreRfcPepm re-applies it on write).
  pepm: z.string().optional(),
});

// read/write only available for internal admins
export const RfcInternalDataSchema = z.object({
  groupNumber: z.string().max(200, 'Group number is too long'),
  productCodes: z
    .array(z.object({value: z.string().max(200, 'Product code name is too long')}))
    .max(100, 'You can add up to 100 product codes'),
  carrierName: z.enum(['AMERICAN_GLOBAL', 'CLEAR_SPRING_HEALTH', 'SELF_FUNDED', 'OTHER'], {
    required_error: 'Carrier name selection is required',
    invalid_type_error: 'Carrier name selection is invalid',
  }),
  carrierNameOther: z.string().max(200, 'Carrier name is too long'),
  telemedicine: z.enum(['CLEVER_HEALTH', 'OTHER'], {
    required_error: 'Telemedicine selection is required',
    invalid_type_error: 'Telemedicine selection is invalid',
  }),
  telemedicineOther: z.string().max(200, 'Telemedicine name is too long'),
  pbm: z.enum(['C3RX', 'VERUS', 'OTHER']),
  pbmOther: z.string().max(200, 'PBM name is too long'),
  app: z.enum(['CLEVER', 'OTHER'], {
    required_error: 'App selection is required',
    invalid_type_error: 'App selection is invalid',
  }),
  appOther: z.string().max(200, 'App name is too long'),
  mec: z.enum(['CHAMPION_HEALTH', 'CHAMPION_HEALTH_HSA', 'HEALTHCUES', 'QUANTUM', 'OTHER'], {
    required_error: 'MEC selection is required',
    invalid_type_error: 'MEC selection is invalid',
  }),
  mecOther: z.string().max(200, 'MEC name is too long'),
  ppoNetwork: z.enum(['FIRST_HEALTH', 'PHCS', 'OTHER'], {
    required_error: 'PPO network selection is required',
    invalid_type_error: 'PPO network selection is invalid',
  }),
  ppoNetworkOther: z.string().max(200, 'PPO network name is too long'),
});

export const groupInfoFinalSchema = z.object({
  benefitYear: z.string().regex(/^\d{4}$/, 'Benefit year must be a 4-digit year'),
  companyName: z
    .string()
    .min(1, 'Company legal name is required')
    .max(200, 'Company legal name is too long'),
  companyDba: z.string().max(200, 'DBA name is too long'),
  otherKnownName: z.string().max(200, 'Other known company name is too long'),
  sic: z.union(
    [z.string().regex(SIC_REGEX, 'SIC must be exactly 4 digits or left blank'), z.literal('')],
    {
      invalid_type_error: 'SIC must be exactly 4 digits or left blank',
      required_error: 'SIC must be exactly 4 digits or left blank',
    },
  ),
  stateOfDoingBusiness: z
    .string()
    .regex(STATE_REGEX, 'Primary state of doing business is required'),
  totalEmployees: z
    .number({
      invalid_type_error: 'Total employees cannot be blank',
      required_error: 'Total employees cannot be blank',
    })
    .int('Total employees must be a number')
    .min(0, 'Total employees must be 0 or greater'),
  numberOfLocations: z
    .number({
      invalid_type_error: 'Number of locations cannot be blank',
      required_error: 'Number of locations cannot be blank',
    })
    .int('Number of locations must be a whole number')
    .min(1, 'At least one location is required'),
  locations: z
    .array(
      z.object({
        locationName: z.string().max(200, 'Location name is too long'),
        fein: z.union([
          z.string().regex(FEIN_REGEX, 'Location FEIN must be exactly 9 digits'),
          z.literal(''),
        ]),
        payrollLocationCode: z.string().max(100, 'Payroll location code is too long').default(''),
        companyAddress: getOptionalAddressFinalSchema('Location company address'),
        mailingAddress: getOptionalAddressFinalSchema('Location mailing address'),
        contactPerson: getOptionalContactFinalSchema('Location contact'),
        achForm: AchFormDraftSchema,
      }),
    )
    .min(1, 'At least one location is required')
    .max(100, 'You can add up to 100 locations')
    // Only the primary location must be complete: its FEIN is the company FEIN
    // and its company address (street/city/state/zip) is required. Additional
    // locations can be completed later.
    .superRefine((locations, ctx) => {
      const primary = locations[0];
      if (!primary) return;
      if (!FEIN_REGEX.test(primary.fein)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Company FEIN (primary location) must be exactly 9 digits',
          path: [0, 'fein'],
        });
      }
      const result = getAddressFinalSchema('Primary location address').safeParse(
        primary.companyAddress,
      );
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: [0, 'companyAddress', ...issue.path],
          });
        }
      }
    }),
});

export const AdminDetailsFinalSchema = z.object({
  eligibility: z
    .object({
      fullTime: z.boolean({
        invalid_type_error:
          'Full-time and/or part-time eligible for coverage selection is required',
        required_error: 'Full-time and/or part-time eligible for coverage selection is required',
      }),
      partTime: z.boolean({
        invalid_type_error:
          'Full-time and/or part-time eligible for coverage selection is required',
        required_error: 'Full-time and/or part-time eligible for coverage selection is required',
      }),
    })
    .refine((eligibility) => eligibility.fullTime || eligibility.partTime, {
      message: 'At least one eligible employee type must be selected',
    }),
  waitingPeriod: z
    .union([z.literal(14), z.literal(15), z.literal(30), z.literal(60)], {
      invalid_type_error: 'Waiting period must be 14, 15, 30, or 60 days',
    })
    .nullish(),
  payrollFrequencies: z
    .array(
      z.enum(PAYROLL_FREQUENCIES, {
        required_error: 'Payroll frequency is required',
        invalid_type_error: 'Payroll frequency selection is invalid',
      }),
    )
    .min(1, 'At least one payroll frequency must be selected'),
  enrollmentCompany: z.string().max(200, 'Enrollment company name is too long'),
  enrollmentType: z.enum(['AUTO_ENROLL', 'VOLUNTARY'], {
    required_error: 'Enrollment type is required',
    invalid_type_error: 'Enrollment type selection is invalid',
  }),
  billingPreOrPostTax: z.enum(['PRE_TAX', 'POST_TAX'], {
    required_error: 'Pre/Post tax premium selection is required',
    invalid_type_error: 'Pre/Post tax premium selection is invalid',
  }),
  benefitsPreOrPostTax: z.enum(['PRE_TAX', 'POST_TAX'], {
    required_error: 'Pre/Post tax benefits selection is required',
    invalid_type_error: 'Pre/Post tax benefits selection is invalid',
  }),
});

export const ContactsFinalSchema = z.object({
  authorizedSigner: getRequiredContactFinalSchema('Plan administrator (authorized signer)'),
  eligibilityContact: getOptionalContactFinalSchema('Eligibility contact'),
  billingContact: getRequiredContactFinalSchema('Billing contact'),
});

export const PayrollInfoFinalSchema = z.object({
  contact: getOptionalContactFinalSchema('Internal payroll contact'),
  vendor: z.string().max(100, 'Payroll vendor name is too long'),
  payrollVendorContact: getOptionalContactFinalSchema('Payroll vendor contact'),
  system: z.string().max(100, 'Payroll system name is too long'),
  payrollCompanyId: z.string().max(100, 'Payroll company ID is too long'),
});

// read/write only available for internal/client admins
export const AgentOfRecordFinalSchema = z.object({
  firstName: z
    .string()
    .min(1, 'Agent first name is required')
    .max(100, 'Agent first name is too long'),
  lastName: z
    .string()
    .min(1, 'Agent last name is required')
    .max(100, 'Agent last name is too long'),
  phone: z
    .string()
    .regex(PHONE_REGEX, 'Agent of record phone number must be a valid US phone number'),
  email: z.string().email('Agent of record email must be valid'),
  additionalPhone: z.union([
    z.literal(''),
    z
      .string()
      .regex(
        PHONE_REGEX,
        'Additional agent phone number must be a valid US phone number or left blank',
      ),
  ]),
  additionalEmail: z.union([
    z.literal(''),
    z.string().email('Additional agent email must be a valid email or left blank'),
  ]),
  pepm: z.string().max(100, 'PEPM is too long'),
});

export const rfcSection125FormDraftSchema = z.object({
  // Group Information
  groupInfo: GroupInfoDraftSchema,
  // Eligibility and Admin
  admin: AdminDetailsDraftSchema,
  // Contacts
  contacts: ContactsDraftSchema,
  // Payroll
  payrollInfo: PayrollInfoDraftSchema,
  // Agent of Record
  agentOfRecord: AgentOfRecordDraftSchema,
});

export const rfcMecFormDraftSchema = z.object({
  // Group Information
  groupInfo: GroupInfoDraftSchema,
  // Eligibility and Admin
  admin: AdminDetailsDraftSchema,
  // Contacts
  contacts: ContactsDraftSchema,
  // Payroll
  payrollInfo: PayrollInfoDraftSchema,
  // Agent of Record
  agentOfRecord: AgentOfRecordDraftSchema,
});

// ACH data lives per-location inside groupInfo.locations[].achForm
// These exports kept for type references in display components
export const rfcAchFormDataDraftSchema = AchFormDraftSchema;

export const rfcClientPortalAccessFormDraftSchema = z.object({
  users: z.array(ContactDraftSchema),
});

// Portal users are fully optional; email/phone are validated only when provided.
const clientPortalUserFinalSchema = getOptionalContactFinalSchema('Client Portal Access User');

export const rfcClientPortalAccessFormFinalSchema = z.object({
  users: z.array(clientPortalUserFinalSchema).max(20, 'You can add up to 20 users'),
});

export const rfcSection125FormFinalSchema = z.object({
  // Group Information
  groupInfo: groupInfoFinalSchema,
  // Eligibility and Admin
  admin: AdminDetailsFinalSchema,
  // Contacts
  contacts: ContactsFinalSchema,
  // Payroll
  payrollInfo: PayrollInfoFinalSchema,
  // Agent of Record
  agentOfRecord: AgentOfRecordFinalSchema,
});

export const rfcMecFormFinalSchema = z.object({
  // Group Information
  groupInfo: groupInfoFinalSchema,
  // Eligibility and Admin
  admin: AdminDetailsFinalSchema,
  // Contacts
  contacts: ContactsFinalSchema,
  // Payroll
  payrollInfo: PayrollInfoFinalSchema,
  // Agent of Record
  agentOfRecord: AgentOfRecordFinalSchema,
});

// ACH is optional - users can skip this section entirely
export const rfcAchFormDataFinalSchema = rfcAchFormDataDraftSchema;

export type RfcSection125Form = z.infer<typeof rfcSection125FormDraftSchema>;
export type RfcMecForm = z.infer<typeof rfcMecFormDraftSchema>;
export type RfcAchForm = z.infer<typeof rfcAchFormDataDraftSchema>;
export type RfcInternalData = z.infer<typeof RfcInternalDataSchema>;
export type RfcClientPortalAccessForm = z.infer<typeof rfcClientPortalAccessFormDraftSchema>;
export type RfcLocationForm = z.infer<typeof LocationDraftSchema>;

export function getBlankAchForm(): z.infer<typeof AchFormDraftSchema> {
  return {
    bankAccount: {
      businessNameOnAccount: '',
      accountHolderFirstName: '',
      accountHolderLastName: '',
      contactPhone: '',
      contactEmail: '',
      address: {street: '', city: '', state: '', zip: ''},
    },
    achDetails: {
      bankName: '',
      bankAddress: {street: '', city: '', state: '', zip: ''},
      routingNumber: '',
      accountNumber: '',
    },
    authorization: {
      authorizedSignature: '',
      signatureDate: '',
    },
  };
}

export function getBlankLocation(): RfcLocationForm {
  return {
    locationName: '',
    fein: '',
    payrollLocationCode: '',
    companyAddress: {street: '', city: '', state: '', zip: ''},
    mailingAddress: {street: '', city: '', state: '', zip: ''},
    contactPerson: {firstName: '', lastName: '', title: '', phone: '', email: ''},
    achForm: getBlankAchForm(),
  };
}

export function getBlankRfcFormState(): {
  section125Form: RfcSection125Form;
  mecForm: RfcMecForm;
  clientPortalAccessForm: RfcClientPortalAccessForm;
} {
  const section125Form = {
    groupInfo: {
      benefitYear: '',
      companyName: '',
      companyDba: '',
      otherKnownName: '',
      sic: '',
      stateOfDoingBusiness: '',
      totalEmployees: 0,
      numberOfLocations: 1,
      locations: [getBlankLocation()],
    },
    admin: {
      // Eligibility starts unselected — the final schema requires the user to
      // explicitly pick at least one employee type before submitting.
      eligibility: {
        fullTime: false,
        partTime: false,
      },
      // Optional field — starts unselected (undefined) rather than silently
      // defaulting. null is reserved for an explicit "No waiting period" choice.
      waitingPeriod: undefined,
      payrollFrequencies: ['BI_WEEKLY'] as PayrollFrequency[],
      enrollmentCompany: '',
      enrollmentType: 'AUTO_ENROLL' as const,
      billingPreOrPostTax: 'PRE_TAX' as const,
      benefitsPreOrPostTax: 'PRE_TAX' as const,
    },
    contacts: {
      authorizedSigner: {
        firstName: '',
        lastName: '',
        title: '',
        phone: '',
        email: '',
      },
      eligibilityContact: {
        firstName: '',
        lastName: '',
        title: '',
        phone: '',
        email: '',
      },
      billingContact: {
        firstName: '',
        lastName: '',
        title: '',
        phone: '',
        email: '',
      },
    },
    payrollInfo: {
      contact: {
        firstName: '',
        lastName: '',
        title: '',
        phone: '',
        email: '',
      },
      vendor: '',
      payrollVendorContact: {
        firstName: '',
        lastName: '',
        title: '',
        phone: '',
        email: '',
      },
      system: '',
      payrollCompanyId: '',
    },
    agentOfRecord: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      additionalPhone: '',
      additionalEmail: '',
      pepm: '',
    },
  };

  return {
    section125Form,
    mecForm: {
      groupInfo: structuredClone(section125Form.groupInfo),
      admin: structuredClone(section125Form.admin),
      contacts: structuredClone(section125Form.contacts),
      payrollInfo: structuredClone(section125Form.payrollInfo),
      agentOfRecord: structuredClone(section125Form.agentOfRecord),
    },
    clientPortalAccessForm: {
      users: [],
    },
  };
}

// Representative, fully-valid RFC form data for testing/demo. Every value here
// satisfies the *final* (submit-time) schemas — phone/email/state/zip/FEIN/SIC
// formats, required contacts, allowed waiting period, etc. — so a tester can hit
// "Use sample data" and submit without fixing validation errors. The MEC form is
// cloned from the 125 form (same shape, like getBlankRfcFormState).
export function getSampleRfcFormState(): {
  section125Form: RfcSection125Form;
  mecForm: RfcMecForm;
  clientPortalAccessForm: RfcClientPortalAccessForm;
} {
  const sampleContact = (
    firstName: string,
    lastName: string,
    title: string,
    phone: string,
    email: string,
  ) => ({firstName, lastName, title, phone, email});

  const sampleAddress = {
    street: '100 Congress Ave',
    city: 'Austin',
    state: 'TX',
    zip: '78701',
  };

  const section125Form: RfcSection125Form = {
    groupInfo: {
      benefitYear: String(new Date().getFullYear() + 1),
      companyName: 'Acme Manufacturing, Inc.',
      companyDba: 'Acme Mfg',
      otherKnownName: '',
      sic: '3559',
      stateOfDoingBusiness: 'TX',
      totalEmployees: 125,
      numberOfLocations: 1,
      locations: [
        {
          locationName: 'Headquarters',
          fein: '123456789',
          payrollLocationCode: 'ACME-HQ-001',
          companyAddress: {...sampleAddress},
          mailingAddress: {...sampleAddress},
          contactPerson: sampleContact(
            'Dana',
            'Rivera',
            'Office Manager',
            '(512) 555-0142',
            'dana.rivera@acme-mfg.example.com',
          ),
          achForm: {
            bankAccount: {
              businessNameOnAccount: 'Acme Manufacturing, Inc.',
              accountHolderFirstName: 'Dana',
              accountHolderLastName: 'Rivera',
              contactPhone: '(512) 555-0142',
              contactEmail: 'dana.rivera@acme-mfg.example.com',
              address: {...sampleAddress},
            },
            achDetails: {
              bankName: 'First Texas Bank',
              bankAddress: {...sampleAddress},
              routingNumber: '111000025',
              accountNumber: '000123456789',
            },
            authorization: {
              authorizedSignature: 'Dana Rivera',
              signatureDate: new Date().toISOString().slice(0, 10),
            },
          },
        },
      ],
    },
    admin: {
      eligibility: {fullTime: true, partTime: false},
      waitingPeriod: 30 as AllowedWaitingPeriodDays,
      payrollFrequencies: ['BI_WEEKLY'] as PayrollFrequency[],
      enrollmentCompany: 'Acme Manufacturing, Inc.',
      enrollmentType: 'AUTO_ENROLL' as const,
      billingPreOrPostTax: 'PRE_TAX' as const,
      benefitsPreOrPostTax: 'PRE_TAX' as const,
    },
    contacts: {
      authorizedSigner: sampleContact(
        'Morgan',
        'Lee',
        'CEO',
        '(512) 555-0110',
        'morgan.lee@acme-mfg.example.com',
      ),
      eligibilityContact: sampleContact(
        'Priya',
        'Shah',
        'HR Director',
        '(512) 555-0125',
        'priya.shah@acme-mfg.example.com',
      ),
      billingContact: sampleContact(
        'Chris',
        'Nguyen',
        'Controller',
        '(512) 555-0137',
        'chris.nguyen@acme-mfg.example.com',
      ),
    },
    payrollInfo: {
      contact: sampleContact(
        'Priya',
        'Shah',
        'HR Director',
        '(512) 555-0125',
        'priya.shah@acme-mfg.example.com',
      ),
      vendor: 'ADP',
      payrollVendorContact: sampleContact(
        'Sam',
        'Carter',
        'Account Rep',
        '(800) 555-0199',
        'sam.carter@adp.example.com',
      ),
      system: 'ADP - Next Generation',
      payrollCompanyId: 'ACME-001',
    },
    agentOfRecord: {
      firstName: 'Jordan',
      lastName: 'Bailey',
      phone: '(512) 555-0150',
      email: 'jordan.bailey@brokerage.example.com',
      additionalPhone: '',
      additionalEmail: '',
      pepm: '5.00',
    },
  };

  return {
    section125Form,
    mecForm: {
      groupInfo: structuredClone(section125Form.groupInfo),
      admin: structuredClone(section125Form.admin),
      contacts: structuredClone(section125Form.contacts),
      payrollInfo: structuredClone(section125Form.payrollInfo),
      agentOfRecord: structuredClone(section125Form.agentOfRecord),
    },
    clientPortalAccessForm: {
      users: [
        sampleContact(
          'Priya',
          'Shah',
          'HR Director',
          '(512) 555-0125',
          'priya.shah@acme-mfg.example.com',
        ),
      ],
    },
  };
}

// Derive an Agent of Record draft from a commission agent (broker) record, so
// an account's RFC can default its agent section to the assigned broker.
// Prefers the broker's explicit contact name; falls back to splitting the
// display name on its first space.
export function buildAgentOfRecordFromBroker(broker: {
  name: string;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}): RfcSection125Form['agentOfRecord'] {
  const [nameFirst = '', ...nameRest] = broker.name.trim().split(/\s+/);
  return {
    firstName: broker.contactFirstName?.trim() || nameFirst,
    lastName: broker.contactLastName?.trim() || nameRest.join(' '),
    phone: broker.contactPhone?.trim() ?? '',
    email: broker.contactEmail?.trim() ?? '',
    additionalPhone: '',
    additionalEmail: '',
    pepm: '',
  };
}

export function getBlankInternalRfcFormState(): RfcInternalData {
  return {
    groupNumber: '',
    productCodes: [],
    carrierName: 'OTHER',
    carrierNameOther: '',
    telemedicine: 'CLEVER_HEALTH',
    telemedicineOther: '',
    pbm: 'C3RX',
    pbmOther: '',
    app: 'CLEVER',
    appOther: '',
    mec: 'OTHER',
    mecOther: '',
    ppoNetwork: 'OTHER',
    ppoNetworkOther: '',
  };
}

export const PRESET_PAYROLL_VENDOR_OPTIONS = [
  {vendor: 'ADP', systems: ['ADP - Homebase', 'ADP - Next Generation']},
  {vendor: 'Quickbooks', systems: []},
  {vendor: 'UKG', systems: ['UKG Pro', 'UKG Ready']},
];
